import React, { useRef, useContext } from 'react';
import { GameDataContext } from '../providers/GameDataProvider';
import { CommunityDragonContext } from '../providers/CommunityDragonProvider';
import { Line } from 'react-chartjs-2';
import { DragonType, JungleCampName, Team } from '../types/snapshot/Enums';
import { StructureName } from '../types/all-game-data/Enums';
import BlueTurret from '../icons/blue_turret.png';
import RedTurret from '../icons/red_turret.png';
import BlueInhib from '../icons/blue_inhib.png';
import RedInhib from '../icons/red_inhib.png';
import Baron from '../icons/baron.png';
import Herald from '../icons/herald.png';
import Horde from '../icons/horde.png';
import DragonElder from '../icons/dragon_elder.png';
import DragonCloud from '../icons/dragon_cloud.png';
import DragonMountain from '../icons/dragon_mountain.png';
import DragonInfernal from '../icons/dragon_infernal.png';
import DragonOcean from '../icons/dragon_ocean.png';
import DragonHextech from '../icons/dragon_hextech.png';
import DragonChemtech from '../icons/dragon_chemtech.png';
var getIcons = function (gameData, championSummary) {
    var icons = [];
    gameData.events.forEach(function (event) {
        var _a;
        var index = gameData.snapshots.findIndex(function (snapshot) { return snapshot.time >= event.time; });
        if (index < 1) {
            return;
        }
        var winrate = gameData.snapshots[index - 1].winrate + (event.time - gameData.snapshots[index - 1].time) / (gameData.snapshots[index].time - gameData.snapshots[index - 1].time) * (gameData.snapshots[index].winrate - gameData.snapshots[index - 1].winrate);
        if (event.victimName === JungleCampName.BARON) {
            icons.push({ icon: Baron, type: 'neutralkill', x: event.time, y: winrate, show: true });
        }
        else if (event.victimName === JungleCampName.RIFT_HERALD) {
            icons.push({ icon: Herald, type: 'neutralkill', x: event.time, y: winrate, show: true });
        }
        else if (event.victimName === JungleCampName.GRUB_1 || event.victimName === JungleCampName.GRUB_2 || event.victimName === JungleCampName.GRUB_3) {
            icons.push({ icon: Horde, type: 'neutralkill', x: event.time, y: winrate, show: true });
        }
        else if (event.victimName.endsWith(' dragon')) {
            var icon = void 0;
            var dragonType = event.victimName.split(' ')[0];
            switch (dragonType) {
                case DragonType.CLOUD:
                    icon = DragonCloud;
                    break;
                case DragonType.MOUNTAIN:
                    icon = DragonMountain;
                    break;
                case DragonType.INFERNAL:
                    icon = DragonInfernal;
                    break;
                case DragonType.OCEAN:
                    icon = DragonOcean;
                    break;
                case DragonType.HEXTECH:
                    icon = DragonHextech;
                    break;
                case DragonType.CHEMTECH:
                    icon = DragonChemtech;
                    break;
                case DragonType.ELDER:
                    icon = DragonElder;
                    break;
                default:
                    return;
            }
            icons.push({ icon: icon, type: 'neutralkill', x: event.time, y: winrate, show: true });
        }
        else if (StructureName.isStructure(event.victimName)) {
            var turret = StructureName.isTurret(event.victimName);
            var icon = void 0;
            if (turret) {
                icon = event.victimTeam === Team.BLUE ? BlueTurret : RedTurret;
            }
            else {
                icon = event.victimTeam === Team.BLUE ? BlueInhib : RedInhib;
            }
            icons.push({ icon: icon, type: 'towerkill', x: event.time, y: winrate, show: true });
        }
        else {
            var championId = (_a = championSummary.find(function (champion) { return champion.alias.toLowerCase() === event.victimName; })) === null || _a === void 0 ? void 0 : _a.id;
            var icon = championId ? "https://raw.communitydragon.org/latest/plugins/rcp-be-lol-game-data/global/default/v1/champion-icons/".concat(championId, ".png") : event.victimName;
            var outlineColor = event.victimTeam === Team.BLUE ? 'rgb(0, 163, 240)' : 'rgb(230, 10, 20)';
            icons.push({ icon: icon, type: 'championkill', x: event.time, y: winrate, show: true, circle: true, outlineColor: outlineColor });
        }
    });
    return icons;
};
var WinrateLineChart = function () {
    var gameData = useContext(GameDataContext);
    var championSummary = useContext(CommunityDragonContext).championSummary;
    var blueLine = 'rgba(0, 163, 240)';
    var redLine = 'rgba(230, 10, 20, 0.8)';
    var blueBg = 'rgba(0, 163, 240, 0.4)';
    var redBg = 'rgba(230, 10, 20, 0.4)';
    var lineColor = gameData.playerTeam === Team.BLUE ? blueLine : redLine;
    var belowBg = gameData.playerTeam === Team.BLUE ? blueBg : redBg;
    var aboveBg = gameData.playerTeam === Team.BLUE ? redBg : blueBg;
    var chartRef = useRef(null);
    var icons = getIcons(gameData, championSummary);
    var toggleEvents = function (type) {
        icons.forEach(function (icon) {
            if (icon.type === type) {
                icon.show = !icon.show;
            }
        });
        chartRef.current.update();
    };
    var chartData = {
        labels: gameData.snapshots.map(function (snapshot) { return snapshot.time; }),
        datasets: [
            {
                label: 'Winrate',
                data: gameData.snapshots.map(function (snapshot) { return snapshot.winrate; }),
                fill: 'start',
                backgroundColor: belowBg,
                borderColor: lineColor,
                borderWidth: 2,
                pointRadius: 2,
                hoverRadius: 6,
                pointHoverBackgroundColor: 'white',
            },
            {
                label: 'Overlay',
                data: gameData.snapshots.map(function () { return (100 - 0) * 2 + 1; }),
                fill: '-1',
                borderColor: 'rgba(0, 0, 0, 0)',
                backgroundColor: aboveBg,
                borderWidth: 0,
                pointRadius: 0,
            }
        ],
    };
    var options = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'index',
            intersect: false,
        },
        scales: {
            x: {
                type: 'linear',
                ticks: {
                    callback: function (value) {
                        var minutes = Math.floor(value / 60);
                        var seconds = Math.floor(value % 60);
                        return "".concat(minutes, ":").concat(seconds < 10 ? '0' : '').concat(seconds);
                    },
                    stepSize: 60,
                    color: '#FFFFFF',
                },
                grid: {
                    display: true,
                    color: 'rgba(255, 255, 255, 0.1)',
                },
            },
            y: {
                beginAtZero: false,
                min: 0,
                max: 100,
                title: {
                    display: true,
                    text: 'Winrate',
                    color: '#FFFFFF',
                    font: {
                        size: 14,
                    }
                },
                ticks: {
                    color: '#FFFFFF',
                    callback: function (value) { return "".concat(value, "%"); },
                    stepSize: 10,
                },
                grid: {
                    display: true,
                    color: function (context) {
                        if (context.tick.value === 50) {
                            return 'rgba(255, 255, 255, 0.8)';
                        }
                        else {
                            return 'rgba(255, 255, 255, 0.2)';
                        }
                    }
                },
            },
        },
        plugins: {
            legend: {
                display: false,
            },
            title: {
                display: false,
            },
            tooltip: {
                mode: 'nearest',
                axis: 'x',
                intersect: false,
                filter: function (tooltipItem) {
                    return tooltipItem.datasetIndex === 0;
                },
                callbacks: {
                    title: function (tooltipItems) {
                        var value = tooltipItems[0].parsed.x;
                        var minutes = Math.floor(value / 60);
                        var seconds = Math.floor(value % 60);
                        return "".concat(minutes, ":").concat(seconds < 10 ? '0' : '').concat(seconds);
                    },
                    label: function (tooltipItem) {
                        var value = tooltipItem.parsed.y;
                        var index = tooltipItem.dataIndex;
                        var dataset = tooltipItem.dataset;
                        var difference = "";
                        if (index > 0) {
                            var previousValue = dataset.data[index - 1];
                            var diff = value - previousValue;
                            var sign = diff >= 0 ? "+" : "";
                            difference = " (".concat(sign).concat(diff.toFixed(1), "%)");
                        }
                        return " ".concat(tooltipItem.dataset.label, ": ").concat(value.toFixed(1), "%").concat(difference);
                    }
                }
            },
            crosshair: {
                line: {
                    color: 'rgba(255, 255, 255, 0.8)',
                    width: 1
                },
                sync: {
                    enabled: false,
                },
                zoom: {
                    enabled: false,
                }
            },
            lineChartIcons: {
                enabled: true,
                icons: icons,
            },
            barChartLabelIcons: {
                enabled: false,
            },
            barChartMiniLabelIcons: {
                enabled: false,
            },
            zoom: {
                pan: {
                    enabled: true,
                    mode: 'x',
                },
                zoom: {
                    wheel: {
                        enabled: true,
                    },
                    pinch: {
                        enabled: true,
                    },
                    mode: 'x',
                },
                limits: {
                    x: { min: 'original', max: 'original' },
                    y: { min: 'original', max: 'original' },
                }
            }
        },
    };
    var buttonStyle = {
        background: 'transparent',
        fontFamily: 'Lora',
        color: 'white',
        height: 30,
        width: 120,
        padding: '4px 10px',
        border: '1px solid white',
        borderRadius: 4,
        margin: '0 5px',
        outline: 'none',
        cursor: 'pointer',
    };
    return (React.createElement("div", { style: { height: '100%', width: '100%' } },
        React.createElement("div", { style: { display: 'flex', justifyContent: 'flex-end', marginRight: 25 } },
            React.createElement("button", { style: buttonStyle, onClick: function () {
                    toggleEvents('championkill');
                } }, "Takedowns"),
            React.createElement("button", { style: buttonStyle, onClick: function () {
                    toggleEvents('towerkill');
                } }, "Towers"),
            React.createElement("button", { style: buttonStyle, onClick: function () {
                    toggleEvents('neutralkill');
                } }, "Neutrals")),
        React.createElement(Line, { data: chartData, options: options, ref: chartRef })));
};
export default WinrateLineChart;
